import arrayMove from 'array-move';
import { isEqual, merge, pick, set } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useRouteMatch } from 'react-router-dom';

import { normalizeWebsiteUrl } from '@mssgme/helpers';
import { useActions, useCollections, useWorkspace } from '@mssgme/hooks';
import { handleError, collectionsApi } from '@mssgme/lib';
import { Notification } from '@mssgme/ui';

export const ACCEPT_IMAGES = ['image/png', 'image/x-png', 'image/jpeg'];

const selectStyles = {
    container: (provided) => ({
        ...provided,
        fontSize: 16,
    }),
    control: (provided) => ({
        ...provided,
        boxShadow: 'none',
        minHeight: 42,
        paddingLeft: 6,
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: 16,
        color: '#8e8e93',
        marginLeft: 0,
    }),
    multiValueLabel: (provided, state) => ({
        ...provided,
        paddingRight: state.data.isFixed ? 6 : 0,
    }),
    multiValueRemove: (provided, state) => ({
        ...provided,
        transform: 'scaleY(.9)',
        display: state.data.isFixed ? 'none' : 'flex',
    }),
};

const selectThemeOverride = (theme) => ({
    ...theme,
    colors: {
        ...theme.colors,
        primary: '#007aff',
        neutral20: '#c7c7cc',
        neutral30: '#8e8e93',
    },
});

// TODO: refactor
export const useProductEdit = () => {
    const { t } = useTranslation('cards');
    const actions = useActions();
    const match = useRouteMatch();
    const workspace = useWorkspace();
    const collectionsStore = useCollections();
    const collections = collectionsStore.items.map((col) => {
        if (col.default) {
            return { ...col, name: t('PageProductsMain.storeFront') };
        }

        return col;
    });
    const history = useHistory();
    const refAmount = useRef(null);
    const productId = (match.params && match.params.productId) || null;
    const collectionId = (match.params && match.params.collectionId) || null;
    const baseurl = workspace.card.profile.absoluteUrl;
    const defaultCurrency = workspace.card.products.defaultCurrency || 'USD';
    const [hasChanges, setHasChanges] = useState(false);
    const [hasPrice, setHasPrice] = useState(true);
    const initialProductCols =
        collections.length > 0
            ? collections
                .filter((col) => collectionId === col.id || col.default)
                .map((col) => ({
                    label: col.name,
                    value: col.id,
                    isFixed: col.default,
                }))
                .sort((a, b) => (a === b ? 0 : a ? -1 : 1))
            : [];
    const [product, setProduct] = useState({
        photos: [],
        name: '',
        slug: '',
        description: '',
        price: {
            amount: null,
            discount: null,
            currency: defaultCurrency,
        },
        published: true,
        externalButton: {
            title: '',
            url: '',
        },
        cols: initialProductCols,
    });
    const [errors, setErrors] = useState({});
    const [hasExternalUrl, setHasExternalUrl] = useState(false);
    const [originalCols, setOriginalCols] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const isNew = !productId;
    const isLoadingProduct = !isNew && productId !== product._id;
    const collectionsOptions = collections.map((col) => ({
        label: col.name,
        value: col.id,
        isFixed: col.default,
    }));

    const loadProduct = async (productId) => {
        try {
            const product = await actions.api.products.retrieve({ productId });
            const collectionsStore = await collectionsApi.list(product.workspace);
            const collections = collectionsStore.items.map((col) => {
                if (col.default) {
                    return { ...col, name: t('PageProductsMain.storeFront') };
                }

                return col;
            });

            setProduct({
                ...product,
                externalButton: {
                    title: (product.externalButton && product.externalButton.title) || '',
                    url: (product.externalButton && product.externalButton.url) || product.externalUrl || '',
                },
                cols:
                    product.cols && !product.cols['null']
                        ? Object.values(product.cols)
                            .map(({ collectionId }) => collections.find((col) => col.id === collectionId))
                            .filter(Boolean)
                            .map((col) => {
                                return {
                                    label: col.name,
                                    value: col.id,
                                    isFixed: col.default,
                                };
                            })
                        : '',
            });
            setOriginalCols(product.cols);
            setHasExternalUrl(
                !!(product.externalButton && product.externalButton.url) || !!product.externalUrl || false
            );
        } catch (e) {
            handleErrors(e);
        }
    };

    const toggleLink = () => {
        setHasExternalUrl(!hasExternalUrl);
    };

    const handleChange = ({ target: { type, name, value: raw, checked } }) => {
        let value = type === 'checkbox' ? checked : raw;

        setProduct(set(merge({}, product), name, value));
        setHasChanges(true);
    };

    const handleCollectionChange = (selected, { action, removedValue }) => {
        switch (action) {
            case 'remove-value':
            case 'pop-value':
                if (removedValue.isFixed) {
                    return;
                }
                break;
        }

        const value = selected ? selected : '';

        setProduct(set(merge({}, product), 'cols', value));
        setHasChanges(true);
    };

    const handlePhotoChange = async ({ target: { files } }) => {
        const currentImagesCount = product.photos.length;
        const images = files && Array.from(files).filter(({ type }) => ACCEPT_IMAGES.includes(type));

        if (!images || images.length === 0) {
            return;
        }

        if (currentImagesCount + images.length > 6) {
            return setErrors({ ...errors, photos: t('productEdit.errors.invalid_photos_count') });
        }

        setIsUploading(true);
        setErrors({ ...errors, photos: '' });

        try {
            const { success: photos, failed } = await actions.api.products.uploadPhotos({
                payloads: images,
            });

            if (failed.length) {
                setErrors({
                    ...errors,
                    photos: failed
                        .map(({ file, error }) => t('errorsList.upload_failed', { file, error: t(error) }))
                        .join('. '),
                });
            }

            if (photos.length) {
                const urls = photos.map(({ result }) => result);

                setProduct({ ...product, photos: product.photos.concat(urls) });
                setHasChanges(hasChanges ? true : !isEqual(product.photos, urls));
            }
        } finally {
            setIsUploading(false);
        }
    };

    const removePhoto = (url) => () => {
        const photos = product.photos.filter((image) => image !== url);

        setProduct({ ...product, photos });
        setHasChanges(true);
    };

    const handleErrors = (e) => {
        if (e.errors) {
            setErrors(e.errors);
        } else {
            handleError(e);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newProduct = {
            ...product,
            externalUrl: product.externalUrl ? normalizeWebsiteUrl(product.externalUrl) : '',
            externalButton: {
                ...product.externalButton,
                url:
                    product.externalButton && product.externalButton.url
                        ? normalizeWebsiteUrl(product.externalButton.url)
                        : '',
            },
        };

        setProduct(newProduct);

        const isNew = !productId;
        const payload = pick(newProduct, [
            'name',
            'description',
            'slug',
            'photos',
            'published',
            'price',
            'externalButton',
        ]);
        payload.cols = '';

        if (product.cols) {
            payload.cols = {};
            product.cols.forEach((col) => {
                if (originalCols && originalCols[col.value] && originalCols[col.value].order) {
                    payload.cols[col.value] = { collectionId: col.value, order: originalCols[col.value].order };
                } else {
                    payload.cols[col.value] = { collectionId: col.value };
                }
            });
        }
        if (!hasPrice || !payload.price || (payload.price && !payload.price.amount)) payload.price = null;
        if (!hasExternalUrl) {
            payload.externalButton = {
                title: null,
                url: null,
            };
        }

        try {
            setIsFetching(true);
            if (isNew) {
                await actions.api.products.create({ payload });
            } else {
                await actions.api.products.update({ productId, payload });
            }

            setHasChanges(false);

            Notification.notify(t('CardEditor.saved'), { showClose: true });
            setIsFetching(false);
            history.goBack();
        } catch (e) {
            setIsFetching(false);
            handleErrors(e);
        }
    };

    const handleRemoveClick = async (e) => {
        e.preventDefault();

        try {
            setIsFetching(true);
            await actions.api.products.delete({ productId });
            setHasChanges(false);
            Notification.notify(t('CardEditor.removed'), { showClose: true });
            setIsFetching(false);
            history.goBack();
        } catch (e) {
            setIsFetching(false);
            handleErrors(e);
        }
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        setProduct({
            ...product,
            photos: arrayMove(product.photos, oldIndex, newIndex),
        });
        setHasChanges(hasChanges ? true : !isEqual(arrayMove(product.photos, oldIndex, newIndex), product.photos));
    };

    useEffect(() => {
        productId && loadProduct(productId);
    }, []);

    return {
        ACCEPT_IMAGES,
        t,
        hasChanges,
        isNew,
        isFetching,
        isLoadingProduct,
        handleSubmit,
        product,
        handleChange,
        baseurl,
        handlePhotoChange,
        removePhoto,
        isUploading,
        onSortEnd,
        errors,
        hasPrice,
        defaultCurrency,
        refAmount,
        collections,
        collectionsOptions,
        selectThemeOverride,
        selectStyles,
        handleCollectionChange,
        hasExternalUrl,
        toggleLink,
        handleRemoveClick,
        history,
    };
};
